import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, type Role } from '../api.ts';
import { t } from '../i18n/index.ts';
import { EvidenceOnboarding } from './evidence-onboarding.tsx';
import { EvidenceActivity } from './evidence-activity.tsx';
import { currentStep, useOnboarding } from '../lib/onboarding.ts';
import { useDemoEvidence } from '../lib/demo-evidence.ts';
import {
  EvidenceReasonRequired,
  addAnnotation,
  buildVerificationBundle,
  clearEvidenceSession,
  extensionFor,
  getAsset,
  getAssetContent,
  getReceipt,
  getSth,
  isPreviewable,
  listAccess,
  listAssets,
  listStuckIngests,
  placeAssetHold,
  previewKind,
  releaseAssetHold,
  retentionPreview,
  retryStuckIngest,
  PREVIEW_MAX_BYTES,
  type AccessRecord,
  type AssetDetail,
  type AssetFilters,
  type EffectiveHold,
  type EvidenceAsset,
  type ReasonChallenge,
} from '../lib/evidence.ts';
import { listCases, type CaseListRow } from '../lib/evidence-workspace.ts';
import {
  Button,
  Cell,
  Empty,
  Field,
  Finding,
  InlineAction,
  Label,
  Metric,
  Metrics,
  Mono,
  PageTitle,
  Row,
  Section,
  Select,
  Spinner,
  Status,
  Table,
  sentence,
  type Tone,
} from '../ui.tsx';
import { Ingest } from './evidence-ingest.tsx';
// The reason-for-access prompt is written once, in the workspace gate, and
// shared: Evidence and the Cases/Disclosure/Redaction surfaces sit behind the
// identical §1.3 seam, so they must ask in identical words.
import { ReasonPrompt } from './evidence-gate.tsx';

/**
 * The Evidence module — the dashboard face of the separately-scoped evidence
 * service (evac-evidence). Everything here is deliberately read-shaped:
 * evidence ARRIVES from capture devices and the ingest API; the dashboard is
 * where a custodian sees what is held, walks its custody record, and hands a
 * court-ready verification bundle to someone who trusts neither us nor them.
 *
 * Three states precede the data, and each is a sentence not an error:
 * unconfigured (the seam is off in this environment), not-enabled (the
 * onboarding moment — an admin turns it on right here), and the
 * reason-for-access prompt (a sensitive read requires a stated reason; the
 * reason is itself recorded — that is the feature).
 */

const STATE_TONE: Record<EvidenceAsset['state'], Tone> = {
  stored: 'safe',
  quarantined: 'attention',
  voided: 'neutral',
  sealed_illegal: 'alert',
  disposed: 'neutral',
};

// Plain-language lifecycle labels. The raw state names (quarantined/stored) are
// storage-engine terms, not the question an operator is asking ("is it in?").
// This renames only the *lifecycle*; the verification claim keeps its precise
// wording everywhere else — STRUCTURE VERIFIED, never a bare "verified", because
// the signing keys are self-attested unless obtained out of band (court-grade
// honesty, CLAUDE.md hard rule 5).
const STATE_LABEL: Record<EvidenceAsset['state'], string> = {
  quarantined: 'Checking',
  stored: 'Saved',
  voided: 'Voided',
  sealed_illegal: 'Sealed',
  disposed: 'Disposed',
};

function formatBytes(n: number): string {
  if (n >= 1_073_741_824) return `${(n / 1_073_741_824).toFixed(1)} GB`;
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function download(name: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * The public zero-install verifier and its plain-language explainer live on the
 * marketing site (site/verify.html, site/status.html) — self-contained, offline,
 * and separately hosted from the dashboard.
 *
 * CONFIGURABLE per environment: set `VITE_VERIFIER_URL` at build time to point
 * production at the real canonical host (e.g. the duress.com/evac verifier). The
 * default below is the current mock deployment on Cloudflare Pages — correct for
 * dev and preview, and overridden for production so a court-facing link never
 * hard-codes the pages.dev mock. This is the single place the dashboard names
 * the verifier host. (See web/docs/verifier-url.md.)
 */
const VERIFIER_URL =
  import.meta.env.VITE_VERIFIER_URL ?? 'https://duress-evac.pages.dev/verify.html';

/**
 * "Why trust this" — the honest one-breath answer to the wedge, on the asset
 * detail itself. It states, in plain language, exactly what the verification
 * proves (structure + signatures) and what it does NOT yet prove (one-true-log
 * via witnesses; the keys are self-attested), reusing the verifier's own verdict
 * vocabulary so the two surfaces never drift. Built as a native <details> so it
 * is keyboard-operable and screen-reader-announced with no JavaScript of ours.
 */
function WhyTrustThis() {
  return (
    <details className="mb-4 rounded-[var(--radius-2)] border border-[var(--line)] bg-[var(--paper-2)]">
      <summary className="cursor-pointer list-none px-3 py-2 text-[14px] font-[600] [&::-webkit-details-marker]:hidden">
        Why you can trust this without trusting us
      </summary>
      <div className="border-t border-[var(--line)] px-3 py-3 text-[14px] leading-6 text-[var(--ink-2)]">
        <p className="mb-2 max-w-prose">
          Anyone holding the bundle can check this evidence{' '}
          <strong className="font-[650] text-[var(--ink)]">without trusting Duress</strong>: the
          recording&rsquo;s segments hash to a sealed Merkle root, the manifest is signed, and the
          manifest is included in an append-only transparency log under a signed tree head.
        </p>
        <p className="mb-3 max-w-prose">
          It does <strong className="font-[650] text-[var(--ink)]">not</strong> yet prove there is
          one true log rather than a different one shown to each reader — independent witness
          cosigning is not established, so the verifier caps at{' '}
          <span className="font-[var(--font-mono)]">STRUCTURE&nbsp;VERIFIED</span>, never a plain
          &ldquo;verified&rdquo; — and the signing keys are self-attested unless you obtained them
          from an independent source.
        </p>
        <a
          href={VERIFIER_URL}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] underline underline-offset-2"
        >
          How verification works, and what it can&rsquo;t check yet
        </a>
      </div>
    </details>
  );
}

/** The honest grade, in a word the inbox can render in a cell. */
function gradeLabel(grade: EvidenceAsset['provenance_grade'] | undefined): string {
  return grade === 'captured'
    ? 'Captured'
    : grade === 'declared'
      ? 'Declared'
      : grade === 'derived'
        ? 'Derived'
        : '—';
}

/** An accessible multiline input — a label-wrapped textarea, styled like Field. */
function NoteInput({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block">
        <Label>{label}</Label>
      </span>
      <textarea
        value={value}
        placeholder={placeholder}
        rows={3}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-3 py-2 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--focus)]"
      />
      {hint ? <span className="mt-1 block text-[13px] text-[var(--ink-2)]">{hint}</span> : null}
    </label>
  );
}

/**
 * Append an operator note to an asset. The copy is deliberate: a note is an
 * APPENDED custody event, never a change to the evidence — overstating that
 * would be the unrecoverable error (CLAUDE.md rule 5). It is recorded on the
 * tamper-evident custody chain and shows on the timeline above.
 */
function AnnotateForm({ orgId, assetId, qc }: { orgId: string; assetId: string; qc: QueryClient }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [added, setAdded] = useState(false);

  const submit = () => {
    const text = note.trim();
    if (text.length === 0) return;
    setBusy(true);
    setError('');
    setAdded(false);
    addAnnotation(orgId, assetId, text)
      .then(() => {
        setNote('');
        setAdded(true);
        return qc.invalidateQueries({ queryKey: ['evidence-asset', orgId, assetId] });
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(false));
  };

  return (
    <div className="mt-5 border-t border-[var(--line)] pt-5">
      <div className="max-w-[560px]">
        <p className="mb-3 text-[13px] text-[var(--ink-2)]">
          Add an operator note. It is appended to the chain of custody as its own record — it does
          not change the evidence, its media, or any earlier custody entry, and it is recorded on
          the same tamper-evident trail.
        </p>
        <NoteInput
          label="Operator note"
          value={note}
          onChange={(v) => {
            setNote(v);
            setAdded(false);
          }}
          placeholder="What you observed, and why it matters…"
          hint="Recorded with your name, on the custody chain. It cannot be edited or deleted once added."
        />
        {error ? (
          <div className="mt-3">
            <Finding tone="alert">{error}</Finding>
          </div>
        ) : null}
        {added ? (
          <div className="mt-3">
            <Finding tone="safe">Note appended to the chain of custody.</Finding>
          </div>
        ) : null}
        <div className="mt-3">
          <Button variant="primary" disabled={busy || note.trim().length === 0} onClick={submit}>
            {busy ? 'Appending…' : 'Append note'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function saveBlob(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * An inline preview of the asset's media — and the honesty rules that bound it.
 *
 * The preview is a **rendering for a human to look at**, never the verified
 * evidence: the label says so, and the bytes shown are the original media
 * fetched as-is through the token seam (no re-encoding, no derived thumbnail).
 * It is drawn from an in-memory `blob:` URL, so nothing is loaded from an
 * external host — the strict CSP holds and the only origin contacted is the
 * evidence service the grant names.
 *
 * Anything not previewable inline — an unsupported type, or a recording past
 * the size ceiling — falls back to a placeholder plus a download of the
 * original, never a broken `<img>`. Every path leads back to the same
 * authoritative artefact: the original bytes.
 */
export function AssetPreview({ orgId, asset }: { orgId: string; asset: AssetDetail['asset'] }) {
  const kind = previewKind(asset.media_type);
  const previewable = isPreviewable(asset);
  const downloadName = `evidence-${asset.id.slice(0, 8)}-original.${extensionFor(asset.media_type)}`;

  const content = useQuery({
    queryKey: ['evidence-content', orgId, asset.id],
    queryFn: () => getAssetContent(orgId, asset.id),
    enabled: previewable,
    retry: false,
    staleTime: Infinity,
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    const blob = content.data;
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setObjectUrl(null);
    };
  }, [content.data]);

  const downloadOriginal = () => {
    if (content.data) {
      saveBlob(downloadName, content.data);
      return;
    }
    void getAssetContent(orgId, asset.id).then((blob) => saveBlob(downloadName, blob));
  };

  const download = (
    <Button
      label={`Download original of evidence ${asset.id.slice(0, 8)}`}
      onClick={downloadOriginal}
    >
      {t('evidence.preview.downloadOriginal')}
    </Button>
  );

  // The honesty line sits above every state — loaded, loading, fallback — so a
  // reader is never looking at rendered media without it.
  const label = (
    <p className="mb-3 max-w-prose text-[13px] text-[var(--ink-2)]">
      {t('evidence.preview.label')}
    </p>
  );

  let body: ReactNode;
  if (!previewable) {
    const reason =
      kind === null
        ? t('evidence.preview.fallback.unsupported', { mediaType: asset.media_type })
        : t('evidence.preview.fallback.tooLarge', { limit: formatBytes(PREVIEW_MAX_BYTES) });
    body = (
      <Empty action={download}>
        {reason} {t('evidence.preview.fallback.download')}
      </Empty>
    );
  } else if (content.isLoading) {
    body = <Spinner>{t('evidence.preview.loading')}</Spinner>;
  } else if (content.error || !objectUrl) {
    body = <Empty action={download}>{t('evidence.preview.error')}</Empty>;
  } else if (kind === 'image') {
    body = (
      <img
        src={objectUrl}
        alt={t('evidence.preview.imageAlt')}
        className="max-h-[480px] max-w-full rounded-[var(--radius-1)] border border-[var(--line)]"
      />
    );
  } else {
    body = (
      // No <track>: evidence footage carries no caption track, and a fabricated
      // empty one would be worse than none. The video is labelled instead.
      <video
        src={objectUrl}
        controls
        aria-label={t('evidence.preview.videoLabel')}
        className="max-h-[480px] max-w-full rounded-[var(--radius-1)] border border-[var(--line)]"
      />
    );
  }

  return (
    <Section title={t('evidence.preview.title')}>
      {label}
      {body}
    </Section>
  );
}

/**
 * The legal-hold panel on an asset. Honest copy is the whole point: a hold
 * prevents disposal and does NOT alter the evidence. Placing and releasing are
 * custodian acts (the service enforces the role); a viewer sees the status.
 */
export function LegalHoldPanel({
  orgId,
  assetId,
  hold,
  canManage,
  onChanged,
}: {
  orgId: string;
  assetId: string;
  hold: EffectiveHold;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const place = async () => {
    setError('');
    setBusy(true);
    try {
      await placeAssetHold(orgId, assetId, reason.trim());
      setReason('');
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    setError('');
    setBusy(true);
    try {
      await releaseAssetHold(orgId, assetId);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // A hold that reaches this asset through a case is released on the case, not
  // here — releasing it per-asset would misrepresent the obligation's scope.
  const viaCase = hold.held && hold.via === 'case';

  return (
    <Section
      title="Legal hold"
      action={
        <Status tone={hold.held ? 'attention' : 'neutral'}>
          {hold.held ? 'Under legal hold' : 'No hold'}
        </Status>
      }
    >
      <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">
        A legal hold prevents this evidence from being disposed of. It does not alter the evidence
        or its chain of custody — placing and lifting a hold are themselves recorded.
      </p>
      {error ? <Finding tone="alert">{error}</Finding> : null}
      {hold.held ? (
        <div className="flex flex-col gap-3">
          <div className="grid max-w-[560px] grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-[14px]">
            <Label>Reason</Label>
            <span>{hold.reason ?? '—'}</span>
            <Label>Placed by</Label>
            <span>
              <Mono>{hold.placed_by ?? '—'}</Mono>
              {hold.placed_at ? ` · ${formatWhen(hold.placed_at)}` : ''}
            </span>
            <Label>Scope</Label>
            <span>{viaCase ? 'Held through a case this asset is linked to' : 'This asset'}</span>
          </div>
          {canManage && !viaCase ? (
            <div>
              <Button variant="secondary" disabled={busy} onClick={() => void release()}>
                {busy ? 'Lifting…' : 'Lift legal hold'}
              </Button>
            </div>
          ) : viaCase ? (
            <p className="text-[13px] text-[var(--ink-2)]">
              Lift this hold from the case that placed it.
            </p>
          ) : (
            <p className="text-[13px] text-[var(--ink-2)]">
              A custodian or admin can lift this hold.
            </p>
          )}
        </div>
      ) : canManage ? (
        <div className="flex max-w-[560px] flex-col gap-3">
          <Field
            label="Reason for the hold"
            value={reason}
            onChange={setReason}
            placeholder="Matter reference, order number…"
            hint="Recorded with the hold and shown to auditors."
          />
          <div>
            <Button
              variant="primary"
              disabled={busy || reason.trim().length === 0}
              onClick={() => void place()}
            >
              {busy ? 'Placing…' : 'Place legal hold'}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-[14px] text-[var(--ink-2)]">
          A custodian or admin can place a legal hold on this evidence.
        </p>
      )}
    </Section>
  );
}

function AssetView({
  orgId,
  assetId,
  role,
  canManage,
  onBack,
}: {
  orgId: string;
  assetId: string;
  role: Role;
  canManage: boolean;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  // Who may append a custody note maps to the evidence 'custodian' role
  // (owner/admin/staff); the service enforces the real gate regardless.
  const canAnnotate = role === 'owner' || role === 'admin' || role === 'staff';
  const detail = useQuery({
    queryKey: ['evidence-asset', orgId, assetId],
    queryFn: () => getAsset(orgId, assetId),
    retry: false,
  });

  if (detail.isLoading) return <Spinner />;
  if (detail.error || !detail.data) {
    return (
      <>
        <div className="mb-4">
          <Button variant="quiet" onClick={onBack}>
            ‹ All evidence
          </Button>
        </div>
        <Section title="This record didn’t open">
          <p className="mb-3 max-w-prose text-[14px]">
            The evidence service didn’t return this record. Go back and open it again; if it keeps
            failing it may still be committing, or an owner or admin can check the evidence service.
          </p>
          <Finding tone="alert">{(detail.error as Error | null)?.message ?? 'Not found'}</Finding>
        </Section>
      </>
    );
  }
  const d: AssetDetail = detail.data;

  const saveBundle = async (kind: string) => {
    setError('');
    const manifest = d.manifests.find((m) => m.kind === kind);
    if (!manifest) return;
    try {
      const bundle = await buildVerificationBundle(orgId, manifest);
      download(`evidence-${d.asset.id.slice(0, 8)}-${kind}-bundle.json`, bundle);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const saveReceipt = async () => {
    setError('');
    try {
      const receipt = await getReceipt(orgId, d.asset.id);
      download(`evidence-${d.asset.id.slice(0, 8)}-receipt.json`, receipt);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <>
      <div className="mb-4">
        <Button variant="quiet" onClick={onBack}>
          {t('evidence.asset.back')}
        </Button>
      </div>
      <Section
        title={t('evidence.asset.record')}
        action={<Status tone={STATE_TONE[d.asset.state]}>{STATE_LABEL[d.asset.state]}</Status>}
      >
        <div className="grid max-w-[720px] grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-[14px]">
          <Label>Identifier</Label>
          <Mono>{d.asset.id}</Mono>
          <Label>Content hash</Label>
          <Mono>{d.asset.merkle_root}</Mono>
          <Label>Captured</Label>
          <span>
            {formatWhen(d.asset.captured_at)}
            {d.asset.device_ref.device_id ? ` · ${d.asset.device_ref.device_id}` : ''}
          </span>
          <Label>Saved</Label>
          <span>{d.asset.committed_at ? formatWhen(d.asset.committed_at) : 'Not yet saved'}</span>
          <Label>Type · size</Label>
          <span>
            {d.asset.media_type} · {formatBytes(d.asset.bytes)}
          </span>
          <Label>Provenance</Label>
          <span>
            {d.asset.provenance_grade === 'captured'
              ? 'Captured — device-presented manifest'
              : d.asset.provenance_grade === 'derived'
                ? 'Derived — produced by the platform from a parent asset'
                : 'Declared — third-party provenance as stated by the uploader'}
          </span>
          <Label>Retained until</Label>
          <span>
            {d.asset.retain_until
              ? new Date(d.asset.retain_until).toLocaleDateString()
              : 'Not yet set'}
          </span>
        </div>
      </Section>

      <AssetPreview orgId={orgId} asset={d.asset} />

      <Section title="Chain of custody">
        <Table head={['When', 'Action', 'Actor']}>
          {d.custody.map((c, i) => (
            <Row key={i}>
              <Cell>{formatWhen(c.at)}</Cell>
              <Cell wide>
                {c.action === 'annotated' ? 'Operator note' : sentence(c.action.replace('_', ' '))}
                {c.justification ? (
                  <span className="mt-1 block whitespace-pre-wrap text-[13px] text-[var(--ink-2)]">
                    {c.action === 'annotated' ? '“' : ''}
                    {c.justification}
                    {c.action === 'annotated' ? '”' : ''}
                  </span>
                ) : null}
              </Cell>
              <Cell>
                <Mono>{c.actor}</Mono>
              </Cell>
            </Row>
          ))}
        </Table>
        {canAnnotate ? <AnnotateForm orgId={orgId} assetId={assetId} qc={qc} /> : null}
      </Section>

      <LegalHoldPanel
        orgId={orgId}
        assetId={d.asset.id}
        hold={d.legal_hold}
        canManage={canManage}
        onChanged={() =>
          void qc.invalidateQueries({ queryKey: ['evidence-asset', orgId, assetId] })
        }
      />

      <Section title={t('evidence.verification.title')}>
        <WhyTrustThis />
        <p className="mb-3 max-w-prose text-[14px] text-[var(--ink-2)]">
          Anyone holding these files can check this evidence without trusting Duress — the bundle
          carries the signed manifest, its place in the public transparency log, and the keys. It is
          the same bundle the offline verifier reads.
        </p>
        {error ? <Finding tone="alert">{error}</Finding> : null}
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void saveBundle('capture')}>Download capture bundle</Button>
          <Button onClick={() => void saveBundle('ingest')}>
            Download countersignature bundle
          </Button>
          <Button onClick={() => void saveReceipt()}>Download commit receipt</Button>
        </div>
      </Section>
    </>
  );
}

/**
 * The read-audit viewer — a tenant-scoped view of who accessed evidence, when,
 * and the reason they stated. The service returns the recorded rows verbatim
 * (a null reason is shown as "—", never inferred), scoped to this org and no
 * other, and it requires a stated reason to read — so this surface may itself
 * raise the reason-for-access prompt, and viewing it is itself audited.
 *
 * Loaded on demand (a button) rather than on render, so opening the Evidence
 * screen does not fire a reason-gated read nobody asked for.
 */
function AccessTrail({ orgId }: { orgId: string }) {
  const [started, setStarted] = useState(false);
  const [records, setRecords] = useState<AccessRecord[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [challenge, setChallenge] = useState<ReasonChallenge | null>(null);

  const load = (from: string | null) => {
    setBusy(true);
    setError('');
    listAccess(orgId, { ...(from ? { cursor: from } : {}), limit: 50 })
      .then((page) => {
        setRecords((prev) => (from ? [...prev, ...page.records] : page.records));
        setCursor(page.nextCursor);
        setChallenge(null);
      })
      .catch((e: unknown) => {
        if (e instanceof EvidenceReasonRequired) setChallenge(e.challenge);
        else setError((e as Error).message);
      })
      .finally(() => setBusy(false));
  };

  if (!started) {
    return (
      <Section title="Access trail">
        <p className="mb-3 max-w-prose text-[14px] text-[var(--ink-2)]">
          Every read of an evidence record is logged: who opened it, when, and the reason they
          stated. Reading this trail is itself a recorded access, so it asks for a reason too.
        </p>
        <Button
          onClick={() => {
            setStarted(true);
            load(null);
          }}
        >
          Show access trail
        </Button>
      </Section>
    );
  }

  if (challenge !== null) {
    return (
      <ReasonPrompt
        challenge={challenge}
        onStated={() => {
          setChallenge(null);
          load(null);
        }}
      />
    );
  }

  return (
    <Section title="Access trail">
      {error ? (
        <div className="mb-3">
          <Finding tone="alert">{error}</Finding>
        </div>
      ) : null}
      {records.length === 0 && !busy && error === '' ? (
        <Empty>
          No access has been recorded yet. The moment someone opens an evidence record, it appears
          here with the reason they stated.
        </Empty>
      ) : (
        <Table head={['When', 'Who', 'Action', 'Resource', 'Reason', 'Outcome']}>
          {records.map((r) => (
            <Row key={r.event_id}>
              <Cell>{formatWhen(r.occurred_at)}</Cell>
              <Cell>
                <Mono>{r.actor_display}</Mono>
              </Cell>
              <Cell>{sentence(r.action.replace('_', ' '))}</Cell>
              <Cell wide>
                <Mono>{r.resource_id === '*' ? 'collection' : r.resource_id.slice(0, 12)}</Mono>
              </Cell>
              <Cell>
                {r.reason ?? (r.reason_class ? sentence(r.reason_class.replaceAll('_', ' ')) : '—')}
              </Cell>
              <Cell>
                <Status
                  tone={
                    r.outcome === 'success'
                      ? 'safe'
                      : r.outcome === 'denied'
                        ? 'attention'
                        : 'alert'
                  }
                >
                  {sentence(r.outcome)}
                </Status>
              </Cell>
            </Row>
          ))}
        </Table>
      )}
      {cursor !== null ? (
        <div className="mt-3">
          <Button disabled={busy} onClick={() => load(cursor)}>
            {busy ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </Section>
  );
}

/**
 * The read-only retention / disposal PREVIEW. It shows which held evidence is
 * past retention and WOULD be eligible for disposal — and what a legal hold is
 * blocking. It deletes nothing, and there is deliberately no disposal control:
 * actual disposal is a separate, consent-gated act the service does not expose.
 */
export function RetentionPreviewView({ orgId, onBack }: { orgId: string; onBack: () => void }) {
  const preview = useQuery({
    queryKey: ['evidence-retention', orgId],
    queryFn: () => retentionPreview(orgId),
    retry: false,
  });

  if (preview.isLoading) return <Spinner />;
  if (preview.error || !preview.data) {
    return (
      <Finding tone="alert">{(preview.error as Error | null)?.message ?? 'Unavailable'}</Finding>
    );
  }
  const p = preview.data;

  return (
    <>
      <div className="mb-4">
        <Button variant="quiet" onClick={onBack}>
          ‹ All evidence
        </Button>
      </div>
      <Section title="Retention and disposal" action={<Status tone="neutral">Preview</Status>}>
        <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">{p.note}</p>
        <div className="mb-6">
          <Metrics>
            <Metric label="Stored evidence" value={p.summary.stored} />
            <Metric label="Past retention" value={p.summary.past_retention} />
            <Metric
              label="Would be eligible"
              value={p.summary.eligible_for_disposal}
              tone={p.summary.eligible_for_disposal > 0 ? 'attention' : 'neutral'}
            />
            <Metric
              label="Blocked by legal hold"
              value={p.summary.blocked_by_legal_hold}
              tone={p.summary.blocked_by_legal_hold > 0 ? 'safe' : 'neutral'}
            />
          </Metrics>
        </div>
        {p.assets.length === 0 ? (
          <Empty>No stored evidence to evaluate yet.</Empty>
        ) : (
          <Table head={['Captured', 'Retain until', 'Content hash', 'Disposal status']}>
            {p.assets.map((a) => (
              <Row key={a.id}>
                <Cell>{formatWhen(a.captured_at)}</Cell>
                <Cell>{a.retain_until ? new Date(a.retain_until).toLocaleDateString() : '—'}</Cell>
                <Cell wide>
                  <Mono>{a.id.slice(0, 8)}…</Mono>
                </Cell>
                <Cell>
                  {a.under_legal_hold ? (
                    <Status tone="safe">
                      Held — disposal blocked{a.hold_via === 'case' ? ' (via case)' : ''}
                    </Status>
                  ) : a.eligible_for_disposal ? (
                    <Status tone="attention">Past retention — would be eligible</Status>
                  ) : (
                    <Status tone="neutral">Within retention</Status>
                  )}
                </Cell>
              </Row>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}

/**
 * Stuck ingests: sessions that never completed. Surfaced so nothing silently
 * vanishes; a custodian can retry, and the retry is safe to repeat — it never
 * creates a duplicate. Renders nothing when there is nothing stuck.
 */
export function StuckIngestsSection({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const stuck = useQuery({
    queryKey: ['evidence-stuck', orgId],
    queryFn: () => listStuckIngests(orgId),
    retry: false,
  });

  // Non-critical surface: stay quiet on load/error, and when nothing is stuck.
  if (!stuck.data || stuck.data.stuck.length === 0) return null;
  const list = stuck.data.stuck;

  const retry = async (sessionId: string) => {
    setMessage('');
    setBusyId(sessionId);
    try {
      const r = await retryStuckIngest(orgId, sessionId);
      setMessage(
        r.resolved
          ? 'Ingest recovered — the evidence is committed.'
          : 'Still incomplete — the upload must be re-sent. Nothing was lost.',
      );
      await qc.invalidateQueries({ queryKey: ['evidence-stuck', orgId] });
      await qc.invalidateQueries({ queryKey: ['evidence-assets', orgId] });
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Section
      title="Stuck ingests"
      action={<Status tone="attention">{list.length} need attention</Status>}
    >
      <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">
        These uploads started but never completed. Nothing has been lost — retrying is safe to
        repeat and will never create a duplicate. An upload that cannot be recovered is flagged for
        re-sending.
      </p>
      {message ? <Finding tone="attention">{message}</Finding> : null}
      <Table head={['Detected', 'Why', 'State', '']}>
        {list.map((s) => (
          <Row key={s.session_id}>
            <Cell>{formatWhen(s.created_at)}</Cell>
            <Cell wide>
              {s.reason === 'open_expired'
                ? 'Upload abandoned before it finished'
                : 'Completion stalled part-way'}
            </Cell>
            <Cell>
              <Status tone={s.dead_letter?.status === 'resolved' ? 'safe' : 'attention'}>
                {sentence((s.dead_letter?.status ?? 'parked').replace('_', ' '))}
              </Status>
            </Cell>
            <Cell>
              {canManage ? (
                <Button
                  variant="secondary"
                  label={`Retry stuck ingest ${s.session_id.slice(0, 8)}`}
                  disabled={busyId === s.session_id}
                  onClick={() => void retry(s.session_id)}
                >
                  {busyId === s.session_id ? 'Retrying…' : 'Retry'}
                </Button>
              ) : null}
            </Cell>
          </Row>
        ))}
      </Table>
    </Section>
  );
}

export function Evidence({
  orgId,
  role,
  onOpenCapture,
  onOpenReports,
}: {
  orgId: string;
  role: Role;
  /** Hands off to the real browser-capture screen for the wizard's ingest step. */
  onOpenCapture?: () => void;
  /**
   * Back to the signed event records under Activity — the other evidence family.
   * This workspace holds court-grade incident recordings; the signed drill and
   * incident reports live there, and the two are cross-linked so neither reads
   * as the whole of "our evidence".
   */
  onOpenReports?: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showRetention, setShowRetention] = useState(false);
  const [challenge, setChallenge] = useState<ReasonChallenge | null>(null);
  const [toggleError, setToggleError] = useState('');
  const [enabling, setEnabling] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stateFilter, setStateFilter] = useState<'all' | EvidenceAsset['state']>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [caseFilter, setCaseFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const isAdmin = role === 'owner' || role === 'admin';
  // The read-audit trail maps to the evidence 'auditor' role (owner/admin/viewer).
  const canAudit = role === 'owner' || role === 'admin' || role === 'viewer';

  const onb = useOnboarding(orgId);
  const demo = useDemoEvidence(orgId);

  /**
   * The real org toggle — the one side-effect the wizard performs. Shared by
   * the wizard's enable step and the compact fallback, so there is exactly one
   * path that turns evidence on.
   */
  const enableEvidence = useCallback(() => {
    setToggleError('');
    setEnabling(true);
    api(`/orgs/${orgId}/evidence`, {
      method: 'PUT',
      body: JSON.stringify({ enabled: true }),
    })
      .then(() => {
        clearEvidenceSession();
        return qc.invalidateQueries({ queryKey: ['evidence-assets', orgId] });
      })
      .catch((e: Error) => setToggleError(e.message))
      .finally(() => setEnabling(false));
  }, [orgId, qc]);

  // The filters are applied SERVER-SIDE (findings #14/#15/#19): they go to the
  // service as query params, so a filter reaches every matching record in the
  // tenant's holding, not just the rows a page happened to load. A capture-date
  // is sent as an inclusive UTC window (start-of-day .. end-of-day), matching
  // the date pickers. The filters are part of the query key, so changing one
  // refetches page one from the server rather than re-slicing stale rows.
  const serverFilters = useMemo<AssetFilters>(() => {
    const f: AssetFilters = {};
    if (stateFilter !== 'all') f.state = stateFilter;
    if (typeFilter !== 'all') f.mediaType = typeFilter;
    if (caseFilter !== 'all') f.caseId = caseFilter;
    if (fromDate !== '') f.capturedAfter = new Date(`${fromDate}T00:00:00Z`).toISOString();
    if (toDate !== '')
      f.capturedBefore = new Date(
        new Date(`${toDate}T00:00:00Z`).getTime() + 86_399_999,
      ).toISOString();
    return f;
  }, [stateFilter, typeFilter, caseFilter, fromDate, toDate]);

  // Keyset pagination: each page carries an opaque next_cursor, and "Load more"
  // fetches the next page and appends it. The reason-for-access challenge is a
  // product state, not an error, so it resolves to an empty, terminal page.
  const assets = useInfiniteQuery({
    queryKey: ['evidence-assets', orgId, serverFilters],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      try {
        return await listAssets(orgId, pageParam, serverFilters);
      } catch (e) {
        if (e instanceof EvidenceReasonRequired) {
          setChallenge(e.challenge);
          return { items: [], next_cursor: null, total: 0, filtered_total: 0 };
        }
        throw e;
      }
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    retry: false,
  });

  const sth = useQuery({
    queryKey: ['evidence-sth', orgId],
    queryFn: () => getSth(orgId),
    enabled: assets.isSuccess && challenge === null,
    retry: false,
  });

  // The case pick-list for the case filter (finding #14) — the inbox can now
  // narrow to a single case server-side. Loaded once the inbox is answering;
  // an empty or failed load simply leaves the filter with just "Any case".
  const casesForFilter = useQuery({
    queryKey: ['evidence-cases', orgId],
    queryFn: () => listCases(orgId),
    enabled: assets.isSuccess && challenge === null,
    retry: false,
  });
  const caseOptions: CaseListRow[] = casesForFilter.data?.items ?? [];

  const err = assets.error as ApiError | Error | null;

  // The seam is off in this environment: quiet, honest, no dead controls.
  if (err instanceof ApiError && err.status === 501) {
    return (
      <>
        <PageTitle sub={t('evidence.subtitle')}>{t('evidence.title')}</PageTitle>
        <Empty>
          Evidence management is not switched on in this environment yet. Nothing is wrong — the
          module appears here the moment the evidence service is connected.
        </Empty>
      </>
    );
  }

  // Onboarding: not enabled for this organisation. Enabling is the only thing
  // to do here, so the first-run wizard is the whole screen — no Skip, because
  // there is nothing to skip to yet.
  if (err instanceof ApiError && err.status === 403 && /not enabled/.test(err.message)) {
    return (
      <>
        <PageTitle sub={t('evidence.subtitle')}>{t('evidence.title')}</PageTitle>
        <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">
          Evidence management gives this organisation a separately-secured store for recordings and
          files that may end up in front of a court: verified ingest, an append-only chain of
          custody, and verification bundles anyone can check without trusting Duress — though the
          bundle&rsquo;s signing keys are self-attested unless independently fetched.
        </p>
        <EvidenceOnboarding
          enabled={false}
          hasAsset={false}
          isAdmin={isAdmin}
          enabling={enabling}
          toggleError={toggleError}
          onEnable={enableEvidence}
          demoLoaded={demo.loaded}
          onLoadDemo={demo.load}
          verifyAcked={onb.verifyAcked}
          onAckVerify={onb.ackVerify}
        />
      </>
    );
  }

  if (err instanceof ApiError && err.status === 403) {
    return (
      <>
        <PageTitle sub={t('evidence.subtitle')}>{t('evidence.title')}</PageTitle>
        <Empty>
          Your role in this organisation has no standing in the evidence service. An owner or admin
          can adjust roles under People.
        </Empty>
      </>
    );
  }

  if (assets.isLoading) return <Spinner />;

  if (challenge !== null) {
    return (
      <>
        <PageTitle sub={t('evidence.subtitle')}>{t('evidence.title')}</PageTitle>
        <ReasonPrompt
          challenge={challenge}
          onStated={() => {
            setChallenge(null);
            void qc.invalidateQueries({ queryKey: ['evidence-assets', orgId] });
          }}
        />
      </>
    );
  }

  if (err) {
    return (
      <>
        <PageTitle sub={t('evidence.subtitle')}>{t('evidence.title')}</PageTitle>
        <Section title="Evidence isn’t loading">
          <p className="mb-3 max-w-prose text-[14px]">
            The evidence service didn’t answer. Nothing has been lost — the record is held by the
            evidence service, not this dashboard. Try again in a moment; if it keeps failing, an
            owner or admin can check the evidence service’s status.
          </p>
          <Finding tone="alert">{err.message}</Finding>
        </Section>
      </>
    );
  }

  if (selected !== null) {
    return (
      <>
        <PageTitle sub={t('evidence.subtitle')}>{t('evidence.title')}</PageTitle>
        <AssetView
          orgId={orgId}
          assetId={selected}
          role={role}
          canManage={isAdmin}
          onBack={() => setSelected(null)}
        />
      </>
    );
  }

  if (showRetention) {
    return (
      <>
        <PageTitle sub={t('evidence.subtitle')}>{t('evidence.title')}</PageTitle>
        <RetentionPreviewView orgId={orgId} onBack={() => setShowRetention(false)} />
      </>
    );
  }

  // The rows are already server-FILTERED and server-COUNTED: `list` is the
  // matching records loaded so far, `total` is every asset the tenant holds
  // (unfiltered) and `filteredTotal` how many match the active filters — both
  // true totals from the service, not a count of what happens to be on screen.
  //
  // Guard every element: the reason-for-access challenge resolves to a page and
  // sets `challenge` asynchronously, so on the resolving render `challenge` can
  // still be null here (the early return above hasn't fired yet). A malformed or
  // empty page must never inject an `undefined` into the list — `a.state` on
  // undefined was the exact throw that blanked the whole app before the boundary
  // and this guard landed.
  const list = (assets.data?.pages ?? []).flatMap((p) => p?.items ?? []).filter(Boolean);
  const total = assets.data?.pages[0]?.total ?? 0;
  const filteredTotal = assets.data?.pages[0]?.filtered_total ?? 0;
  const committed = list.filter((a) => a.state === 'stored').length;
  // The media-type menu lists the types seen in the loaded rows, plus whatever
  // is currently selected (so the active choice never vanishes from its own
  // dropdown). Clearing it back to "Any type" repopulates from the full set.
  const mediaTypes = Array.from(
    new Set([...list.map((a) => a.media_type), ...(typeFilter !== 'all' ? [typeFilter] : [])]),
  ).sort();
  const filterActive =
    stateFilter !== 'all' ||
    typeFilter !== 'all' ||
    caseFilter !== 'all' ||
    fromDate !== '' ||
    toDate !== '';
  const moreToLoad = assets.hasNextPage === true;
  const hasAsset = total > 0;
  const step = currentStep({ enabled: true, hasAsset, verifyAcked: onb.verifyAcked });
  const showWizard = !onb.dismissed && step !== 'done';
  const latest = [...list].sort(
    (a, b) => new Date(b.captured_at).getTime() - new Date(a.captured_at).getTime(),
  )[0];

  return (
    <>
      <PageTitle sub={t('evidence.subtitle')}>{t('evidence.title')}</PageTitle>

      {/*
        The persistent demo marker. Whenever sample data is loaded it is stated,
        loudly and at the top, that those records are illustrations that never
        enter the store — so nothing here can ever be mistaken for real evidence.
      */}
      {demo.loaded ? (
        <Finding tone="attention">
          Sample data is loaded. The records marked <strong className="font-medium">Sample</strong>{' '}
          below are illustrations — they are graded declared, never enter the evidence store, and
          can never be produced as real evidence.
        </Finding>
      ) : null}

      {/* Reachable again after it is skipped or finished. Alongside it, the
          bridge back to the signed drill and incident records — the other half
          of "our evidence" — so this workspace does not read as the whole of it. */}
      {!showWizard ? (
        <div className="mb-4 flex items-center justify-between">
          {onOpenReports ? (
            <InlineAction onClick={onOpenReports}>← Signed event records</InlineAction>
          ) : (
            <span />
          )}
          <InlineAction onClick={onb.reopen}>Setup guide</InlineAction>
        </div>
      ) : null}

      {showWizard ? (
        <EvidenceOnboarding
          enabled
          hasAsset={hasAsset}
          isAdmin={isAdmin}
          enabling={enabling}
          toggleError={toggleError}
          onEnable={enableEvidence}
          onOpenCapture={onOpenCapture}
          onOpenLatest={latest ? () => setSelected(latest.id) : undefined}
          demoLoaded={demo.loaded}
          onLoadDemo={demo.load}
          verifyAcked={onb.verifyAcked}
          onAckVerify={onb.ackVerify}
          onSkip={onb.dismiss}
        />
      ) : null}

      <div className="mb-6">
        <Metrics>
          <Metric label="Evidence held" value={total} />
          <Metric
            label="Saved"
            value={committed}
            sub={moreToLoad ? 'in loaded rows' : undefined}
            tone={committed > 0 ? 'safe' : 'neutral'}
          />
          {sth.data ? (
            <Metric
              label="Transparency log"
              value={sth.data.tree_size}
              sub={`entries · head signed ${formatWhen(sth.data.signed_at)}`}
            />
          ) : null}
        </Metrics>
      </div>

      {/* Add-by-upload, for the roles that hold evidence. A viewer or occupant
          never sees the control — the evidence service would refuse the ingest,
          and a dead button is worse than an absent one. */}
      {isAdmin ? (
        uploading ? (
          <Ingest
            orgId={orgId}
            onClose={() => setUploading(false)}
            onCommitted={() => void qc.invalidateQueries({ queryKey: ['evidence-assets', orgId] })}
          />
        ) : (
          <div className="mb-6">
            <Button variant="primary" onClick={() => setUploading(true)}>
              Add evidence
            </Button>
          </div>
        )
      ) : null}

      <StuckIngestsSection orgId={orgId} canManage={isAdmin} />

      <EvidenceActivity
        assets={list}
        demoAssets={demo.assets}
        sth={sth.data}
        onRefresh={() => void assets.refetch()}
        onOpenAsset={setSelected}
      />

      <Section
        title="Evidence"
        action={
          total > 0 ? (
            <span className="flex items-center gap-4">
              <span className="text-[13px] text-[var(--ink-2)]">
                {filterActive ? `${filteredTotal} of ${total}` : `${total} held`}
              </span>
              <Button variant="quiet" onClick={() => setShowRetention(true)}>
                Retention and disposal
              </Button>
            </span>
          ) : null
        }
      >
        {total === 0 ? (
          <Empty>
            No evidence has been ingested yet. Evidence arrives from capture devices, the ingest
            API, and files uploaded here; the moment something is committed it appears with its
            chain of custody.
          </Empty>
        ) : (
          <>
            {/* Filters. State, media type, a capture-date range and a case —
                applied SERVER-SIDE, so they reach every matching record in the
                holding, and the count beside the title is the true total. */}
            <div className="mb-4 flex flex-wrap items-end gap-4">
              <div className="w-[180px]">
                <Select
                  label="State"
                  value={stateFilter}
                  onChange={(v) => setStateFilter(v as 'all' | EvidenceAsset['state'])}
                  options={[
                    { value: 'all', label: 'Any state' },
                    { value: 'quarantined', label: 'Checking' },
                    { value: 'stored', label: 'Saved' },
                    { value: 'voided', label: 'Voided' },
                    { value: 'sealed_illegal', label: 'Sealed (illegal)' },
                    { value: 'disposed', label: 'Disposed' },
                  ]}
                />
              </div>
              <div className="w-[200px]">
                <Select
                  label="Media type"
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={[
                    { value: 'all', label: 'Any type' },
                    ...mediaTypes.map((t) => ({ value: t, label: t })),
                  ]}
                />
              </div>
              <div className="w-[200px]">
                <Select
                  label="Case"
                  value={caseFilter}
                  onChange={setCaseFilter}
                  options={[
                    { value: 'all', label: 'Any case' },
                    ...caseOptions.map((c) => ({
                      value: c.id,
                      label: `${c.title} (${c.linked_count})`,
                    })),
                  ]}
                />
              </div>
              <div className="w-[160px]">
                <Field label="Captured from" type="date" value={fromDate} onChange={setFromDate} />
              </div>
              <div className="w-[160px]">
                <Field label="Captured to" type="date" value={toDate} onChange={setToDate} />
              </div>
              {filterActive ? (
                <InlineAction
                  onClick={() => {
                    setStateFilter('all');
                    setTypeFilter('all');
                    setCaseFilter('all');
                    setFromDate('');
                    setToDate('');
                  }}
                >
                  Clear filters
                </InlineAction>
              ) : null}
            </div>

            {filterActive && moreToLoad ? (
              <Finding tone="neutral">
                Filters are applied across your whole holding, server-side — every matching record
                is reachable, not only those loaded. {list.length} of {filteredTotal} matching
                loaded; “Load more” below brings in the rest.
              </Finding>
            ) : null}

            {filteredTotal === 0 ? (
              <Empty>No evidence matches these filters. Clear them to see everything held.</Empty>
            ) : (
              <Table head={['Captured', 'State', 'Grade', 'Type', 'Size', 'Content hash', '']}>
                {list.map((a) => (
                  <Row key={a.id}>
                    <Cell>{formatWhen(a.captured_at)}</Cell>
                    <Cell>
                      <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
                        <Status tone={STATE_TONE[a.state]}>{STATE_LABEL[a.state]}</Status>
                        {a.under_legal_hold ? <Status tone="attention">Legal hold</Status> : null}
                      </span>
                    </Cell>
                    <Cell>{gradeLabel(a.provenance_grade)}</Cell>
                    <Cell>{a.media_type}</Cell>
                    <Cell numeric>{formatBytes(a.bytes)}</Cell>
                    <Cell wide>
                      <Mono>{a.merkle_root.slice(0, 16)}…</Mono>
                    </Cell>
                    <Cell>
                      <Button
                        variant="quiet"
                        label={`Open evidence ${a.merkle_root.slice(0, 8)}`}
                        onClick={() => setSelected(a.id)}
                      >
                        Open
                      </Button>
                    </Cell>
                  </Row>
                ))}
              </Table>
            )}
            <div className="mt-4 flex items-center justify-center text-[13px] text-[var(--ink-2)]">
              {moreToLoad ? (
                <Button
                  variant="quiet"
                  disabled={assets.isFetchingNextPage}
                  onClick={() => void assets.fetchNextPage()}
                >
                  {assets.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              ) : (
                <span>End of the record · {list.length} shown</span>
              )}
            </div>
          </>
        )}
      </Section>

      {canAudit ? <AccessTrail orgId={orgId} /> : null}

      {/*
        Sample data lives in its own section and its own table — never mixed
        into the real list above. It is graded declared, marked Sample, obvious
        to clear, and it never touches the evidence service.
      */}
      <Section
        title="Sample evidence"
        action={
          demo.loaded ? (
            <Button variant="quiet" onClick={demo.clear} label="Clear all sample data">
              Clear sample data
            </Button>
          ) : null
        }
      >
        <p className="mb-3 max-w-prose text-[14px] text-[var(--ink-2)]">
          A small set of clearly-marked sample records, for learning what the module does. Sample
          data is a local illustration only: it is graded declared, never sent to the evidence
          store, and never counted among the evidence held above.
        </p>
        {demo.loaded ? (
          <Table head={['Captured', 'Grade', 'Type', 'Size', 'Illustrates']}>
            {demo.assets.map((d) => (
              <Row key={d.id}>
                <Cell>{formatWhen(d.captured_at)}</Cell>
                <Cell>
                  <Status tone="neutral">Sample · declared</Status>
                </Cell>
                <Cell>{d.media_type}</Cell>
                <Cell numeric>{formatBytes(d.bytes)}</Cell>
                <Cell wide>{d.illustrates}</Cell>
              </Row>
            ))}
          </Table>
        ) : (
          <Empty action={<Button onClick={demo.load}>Load sample data</Button>}>
            Nothing is loaded. Loading it records nothing on the evidence service — it only
            populates this screen so you can see how the module reads.
          </Empty>
        )}
      </Section>
    </>
  );
}
